import Fuse from "fuse.js";
import { isEqual } from "lodash";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useState } from "react";
import * as client from "../../client";
import { useToast } from "../../context-providers/Toast";
import {
  useCreateCRMContact,
  useUpdateCRMContact,
  useUpdateReport,
} from "../../pages/specialty-assistant/data";
import { useCRMContactsContext } from "./CRMContactsProvider";
import styles from "./EditCRMContact.module.css";
import { getContactLabel, getOrganizationLabel } from "./orgLabels";
import { makeUpdateRequest, maybeSaveContactAndReassign } from "./utils";

// NOTE: provider-only: `EditCRMContact` requires a surrounding
// `CRMContactsProvider` and will throw if used outside of it.

// Small helper component to reduce repeated label + input markup.
// Placed at module scope so its identity is stable across renders (avoids
// unmount/remount of the input subtree which can steal focus).
const Field: React.FC<{
  htmlFor: string;
  label: string;
  children: React.ReactNode;
}> = ({ htmlFor, label, children }) => (
  <div>
    <label htmlFor={htmlFor} className={styles.sectionLabel}>
      <strong>{label}</strong>
    </label>
    {children}
  </div>
);

type EditCRMContactModalProps = {
  visible: boolean;
  onHide: () => void;
  contact: client.ReportContact;
  reportId: string;
  onSaved?: (contact: client.ReportContact) => void;
};

export const EditCRMContactModal: React.FC<EditCRMContactModalProps> = ({
  visible,
  onHide,
  contact,
  reportId,
  onSaved,
}) => {
  const [editedContact, setEditedContact] = useState(contact);
  // `originalContact` is the snapshot of the contact when the modal opened
  // and is used as the baseline for detecting reassignments.
  const [originalContact, setOriginalContact] = useState(contact);
  const [initialAssigneeId, setInitialAssigneeId] = useState<string | null>(
    contact.id
  );
  const { contactsByEmail } = useCRMContactsContext();
  const contactsArray = useMemo(
    () => (contactsByEmail ? Array.from(contactsByEmail.values()) : []),
    [contactsByEmail]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<client.ReportContact[]>([]);
  const [emailMatchedContact, setEmailMatchedContact] =
    useState<client.ReportContact | null>(null);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const createContactMutation = useCreateCRMContact();
  const updateContactMutation = useUpdateCRMContact();
  const updateReportMutation = useUpdateReport(reportId);

  const contactTypeLabel = getContactLabel(contact);
  const organizationLabel = getOrganizationLabel(contact);
  // TODO: This is working around an issue likely fixed in prime-react 10.5.2+
  // (we are on 10.5.1). Once we upgrade, we can remove this workaround, though
  // we need to test the rest of the UI to ensure no regressions.
  const [resetKey, setResetKey] = useState(0);

  const fuseContacts = useMemo(() => {
    const fuseKeys = [
      { name: "full_name", weight: 2 },
      { name: "email", weight: 2 },
      { name: "organization_name", weight: 1 },
    ];
    return new Fuse<client.ReportContact>(contactsArray, {
      keys: fuseKeys,
      threshold: 0.3,
      includeScore: true,
    });
  }, [contactsArray]);

  useEffect(() => {
    if (visible) {
      setEditedContact(contact);
      setOriginalContact(contact);
      setInitialAssigneeId(contact.id ?? null);
      setEmailMatchedContact(null);
      setSuggestions([]);
      setSearchQuery("");
      setResetKey((x) => x + 1);
    }
  }, [visible]);

  const selectContact = (selectedContact: client.ReportContact) => {
    // Never overwrite the original contact: we use it to determine if we are
    // reassigning or not after editing, etc.
    setEditedContact(selectedContact);
    setOriginalContact(selectedContact);
    setSuggestions([]);
    setSearchQuery("");
  };

  const handleAddNew = () => {
    const emptyContact: client.ReportContact = {
      id: "",
      full_name: "",
      email: "",
      organization_name: null,
      office_phone: null,
      cell_phone: null,
      role: null,
      contact_type: contact.contact_type,
    };

    setEditedContact(emptyContact);
    setSuggestions([]);
    setSearchQuery("");
    setResetKey((x) => x + 1);
  };

  const editedReq = makeUpdateRequest(editedContact);
  const originalReq = makeUpdateRequest(originalContact);
  const hasChanges = !isEqual(editedReq, originalReq);

  const handleSave = async () => {
    setSaving(true);
    try {
      const resultingContact = await maybeSaveContactAndReassign({
        editedContact,
        originalContact,
        emailMatchedContact,
        currentAssigneeId: initialAssigneeId,
        updateReportMutation,
        createContactMutation,
        updateContactMutation,
      });

      if (resultingContact && onSaved) {
        onSaved(resultingContact);
      }

      onHide();
    } catch (err) {
      toast.show({
        severity: "error",
        summary: "Failed to save contact",
        sticky: true,
        icon: "pi pi-times",
      });
    } finally {
      setSaving(false);
    }
  };

  const isAddingNew = editedContact.id === "" && !emailMatchedContact;
  const isReassigning =
    editedContact.id !== (initialAssigneeId ?? "") || emailMatchedContact;

  const saveButtonLabel = isAddingNew
    ? "Add"
    : isReassigning
      ? "Reassign"
      : "Save";

  const isSaveDisabled = isAddingNew
    ? !isEmailValid || !editedContact.email || !editedContact.full_name
    : !hasChanges && !isReassigning;

  return (
    <Dialog
      header={`${contactTypeLabel} Details`}
      visible={visible}
      className={styles.dialog}
      onHide={onHide}
      onClick={(e) => e.stopPropagation()}
      footer={
        <div className={styles.dialogFooter}>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={onHide}
            severity="secondary"
          />
          <Button
            label={saveButtonLabel}
            icon="pi pi-check"
            onClick={handleSave}
            disabled={isSaveDisabled}
            loading={saving}
          />
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        <div>
          <label htmlFor="contact_search" className={styles.sectionLabel}>
            <strong>Change {contactTypeLabel}</strong>
          </label>
          <div className={styles.searchRow}>
            <AutoComplete
              key={resetKey}
              id="contact_search"
              className={styles.autoComplete}
              panelClassName={styles.autoCompletePanel}
              appendTo="self"
              value={searchQuery}
              suggestions={suggestions}
              field="full_name"
              placeholder={`Search by name, email, or ${organizationLabel.toLowerCase()}...`}
              itemTemplate={(item: client.ReportContact) => {
                return (
                  <div>
                    <div className={styles.autoCompleteItemTemplateName}>
                      {item.full_name}
                    </div>
                    <div className={styles.autoCompleteItemTemplateMetadata}>
                      {item.email}
                      {item.organization_name && ` • ${item.organization_name}`}
                    </div>
                  </div>
                );
              }}
              completeMethod={(e) => {
                const q = e.query ?? "";
                if (q && q.length > 0) {
                  const results = fuseContacts
                    .search(q)
                    .map((r) => r.item)
                    .slice(0, 10);
                  setSuggestions(results);
                } else {
                  setSuggestions([]);
                }
              }}
              onSelect={(e) => selectContact(e.value)}
              onChange={(e) => setSearchQuery(e.value)}
              inputClassName={styles.autoCompleteInput}
            />
            <Button
              icon="pi pi-plus"
              onClick={handleAddNew}
              aria-label={`Add new ${contactTypeLabel}`}
            />
          </div>
        </div>

        <div>
          <div className={styles.sectionTitle}>
            {editedContact.id === ""
              ? `New ${contactTypeLabel} Details`
              : editedContact.id !== originalContact.id
                ? `Selected ${contactTypeLabel}`
                : `Current ${contactTypeLabel}`}
          </div>
          <div className={styles.columnContainer}>
            <Field htmlFor="full_name" label="Full Name">
              <InputText
                id="full_name"
                value={editedContact.full_name || ""}
                onChange={(e) =>
                  setEditedContact({
                    ...editedContact,
                    full_name: e.target.value,
                  })
                }
                className={styles.inputFullWidth}
              />
            </Field>

            <Field htmlFor="email" label="Email">
              <InputText
                type="email"
                id="email"
                value={editedContact.email}
                disabled={editedContact.id !== ""}
                readOnly={editedContact.id !== ""}
                onChange={(e) => {
                  // We are doing two things here:
                  //   1. Email validation
                  //   2. Detecting when the typed email matches an existing contact,
                  //      and using that contact instead of creating a new one.
                  const newEmail = e.target.value;
                  const valid = e.target.validity.valid;

                  setEditedContact((prev) => ({ ...prev, email: newEmail }));
                  setIsEmailValid(valid);

                  if (editedContact.id === "" && newEmail) {
                    if (valid) {
                      const exactMatch = contactsByEmail.get(
                        newEmail.toLowerCase()
                      );

                      if (exactMatch) {
                        setEditedContact({ ...exactMatch });
                        setEmailMatchedContact(exactMatch);
                      }
                    }
                  }
                }}
                className={`${styles.inputFullWidth} ${
                  editedContact.id === "" &&
                  !isEmailValid &&
                  editedContact.email
                    ? "p-invalid"
                    : ""
                }`}
                placeholder={
                  editedContact.id === "" ? "email@example.com" : undefined
                }
                required={editedContact.id === ""}
                autoComplete="email"
              />
              {editedContact.id === "" &&
                !isEmailValid &&
                editedContact.email && (
                  <small className={styles.emailWarning}>
                    Please enter a valid email address
                  </small>
                )}
            </Field>

            <Field htmlFor="organization_name" label={organizationLabel}>
              <InputText
                id="organization_name"
                value={editedContact.organization_name ?? ""}
                onChange={(e) =>
                  setEditedContact({
                    ...editedContact,
                    organization_name: e.target.value,
                  })
                }
                placeholder={`${organizationLabel} name`}
                className={styles.inputFullWidth}
              />
            </Field>

            <Field htmlFor="office_phone" label="Office Phone">
              <InputText
                id="office_phone"
                value={editedContact.office_phone ?? ""}
                onChange={(e) =>
                  setEditedContact({
                    ...editedContact,
                    office_phone: e.target.value,
                  })
                }
                className={styles.inputFullWidth}
                placeholder=""
              />
            </Field>

            <Field htmlFor="cell_phone" label="Cell Phone">
              <InputText
                id="cell_phone"
                value={editedContact.cell_phone ?? ""}
                onChange={(e) =>
                  setEditedContact({
                    ...editedContact,
                    cell_phone: e.target.value,
                  })
                }
                className={styles.inputFullWidth}
                placeholder=""
              />
            </Field>

            <Field htmlFor="role" label="Role">
              <InputText
                id="role"
                value={editedContact.role ?? ""}
                onChange={(e) =>
                  setEditedContact({
                    ...editedContact,
                    role: e.target.value,
                  })
                }
                className={styles.inputFullWidth}
                placeholder="e.g., Account Manager"
              />
            </Field>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
